import ChatBubble from "../../../components/ChatBubble";
import ChatHeader from "../../../components/ChatHeader";
import DateDivider from "../../../components/DateDivider";
import MessageBox from "../../../components/MessageBox";
import { SkeletonMessagePanel } from "../../../components/skeleton";
import SenderChatBubble from "../../../components/SenderChatBubble";
import TypingIndicator from "../../../components/TypingIndicator";
import ActionConfirmation from "../../../components/ActionConfirmation";
import RenameGroupDialog from "../../../components/RenameGroupDialog";
import ViewParticipantsDialog from "../../../components/ViewParticipantsDialog";
import type { ChatWindowViewModelState } from "../ChatWindowViewModel";
import { LDSHeading, LDSMessages } from "@resmed/lumina-design-system";
import { INFORMATION_ICON } from "../../../utils/SVGHelper";
import { getTimeOnly } from "../../../utils/DateHelper";
import { useRef, useEffect } from "react";

interface MessagePanelProps {
  vm: ChatWindowViewModelState;
}

const SCROLL_THRESHOLD = 150; // px from bottom — within this, auto-scroll on new messages

const MessagePanel = ({ vm }: MessagePanelProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  // Sentinel element at the end of the list — scrollIntoView is more reliable than scrollTop
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Tracks whether the user is near the bottom; controls auto-scroll on new messages
  const isNearBottomRef = useRef(true);

  const scrollToBottom = () => {
    // Double rAF: first frame commits DOM, second frame has final layout/scrollHeight
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
    });
  };

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    isNearBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD;
  };

  // Always scroll to bottom when switching conversations
  useEffect(() => {
    isNearBottomRef.current = true;
    scrollToBottom();
  }, [vm.selectedId]);

  // Auto-scroll on new messages only if already near the bottom
  useEffect(() => {
    if (!isNearBottomRef.current) return;
    scrollToBottom();
  }, [vm.messages.length]);

  // Scroll to bottom when typing indicator appears (keeps input in view)
  useEffect(() => {
    if (!vm.typing || !isNearBottomRef.current) return;
    scrollToBottom();
  }, [vm.typing]);
  if (!vm.selectedConversation) {
    return (
      <div className="lds-w-[60%] lds-flex lds-flex-1 lds-items-center lds-justify-center lds-bg-white lds-min-w-0 lds-overflow-hidden">
        <LDSHeading size="h5" className="lds-font-normal lds-text-textcolor-caption">
          Select a conversation to view messages
        </LDSHeading>
      </div>
    );
  }

  return (
    <div className="lds-w-[60%] lds-flex lds-flex-col lds-min-w-0 lds-min-h-0 lds-overflow-hidden">
      {/* Chat header — chatroomName takes priority; else use participant summary from API */}
      <ChatHeader
        name={vm.groupName || vm.selectedConversation.chatroomName || vm.participantsSummary || vm.selectedConversation.name}
        icon={vm.selectedConversation.icon}
        onClose={vm.closeConversation}
        onMenuItemSelect={vm.onMenuItemSelect}
        showMenu={!vm.selectedConversation.isArchived}
        pinnedStatus={vm.selectedConversation.pinnedStatus}
        participantsCount={vm.participants.length}
      />

      {/* Messages — min-h-0 allows flex-1 to shrink so overflow-y-auto creates a real scroll boundary */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="lds-flex-1 lds-min-h-0 lds-overflow-y-auto lds-px-6 lds-py-4 lds-flex lds-flex-col lds-gap-6 lds-bg-white"
      >
        {vm.messagesLoading ? (
          <SkeletonMessagePanel />
        ) : vm.messages.length === 0 ? (
          <div className="lds-flex lds-items-center lds-justify-center lds-h-full">
            <p className="lds-text-sm lds-text-textcolor-caption">No messages yet</p>
          </div>
        ) : (
          vm.messages.reduce<React.ReactNode[]>((acc, message, index) => {
          const prevMessage = vm.messages[index - 1];
          const currentDay = message.timestamp.split(" ")[0];
          const prevDay = prevMessage?.timestamp.split(" ")[0];
          if (currentDay !== prevDay) {
            acc.push(
              <DateDivider key={`divider-${message.id}`} timestamp={message.timestamp} />
            );
          }
          acc.push(
            message.type === "system" ? (
              // Administrator/system message — left-aligned with info icon
              <div key={message.id} className="lds-flex lds-flex-col lds-items-start lds-gap-0.5 lds-px-2">
                <div className="lds-flex lds-items-center lds-gap-1.5">
                  <img
                    src={INFORMATION_ICON.src}
                    alt={INFORMATION_ICON.alt}
                    width={INFORMATION_ICON.width}
                    height={INFORMATION_ICON.height}
                    className="lds-opacity-50 lds-shrink-0"
                  />
                  <LDSHeading
                    size="h5"
                    className="!lds-font-sans !lds-text-xs !lds-font-semibold !lds-leading-[125%] !lds-tracking-[0.04em] lds-text-textcolor-weak"
                  >
                    {message.content}
                  </LDSHeading>
                </div>
                <LDSHeading
                  size="h5"
                  className="lds-font-normal lds-text-textcolor-caption lds-text-[10px] lds-opacity-70 lds-pl-5"
                >
                  {getTimeOnly(message.timestamp)}
                </LDSHeading>
              </div>
            ) : message.type === "received" ? (
              <ChatBubble
                key={message.id}
                id={message.id}
                userName={message.userName ?? ""}
                avatarLabel={message.avatarLabel}
                avatarImage={message.avatarImage}
                content={message.content}
                timestamp={message.timestamp}
                calloutData={message.calloutData}
                isDeleted={message.isDeleted}
                hasAttachment={message.hasAttachment}
              />
            ) : (
              <SenderChatBubble
                key={message.id}
                id={message.id}
                content={message.content}
                timestamp={message.timestamp}
                status={message.status ?? "sent"}
                calloutData={message.calloutData}
                isDeleted={message.isDeleted}
                hasAttachment={message.hasAttachment}
              />
            )
          );
          return acc;
        }, [])
        )}
        {/* Sentinel — scrollIntoView target, always at the bottom of the list */}
        <div ref={messagesEndRef} />
      </div>

      {/* Typing indicator — shown when a participant is typing */}
      {vm.typing ? (
        <div className="lds-shrink-0 lds-px-3 lds-py-3 lds-bg-white">
          <TypingIndicator
            text={vm.typing}
            avatarLabel={vm.selectedConversation.avatarLabel}
          />
        </div>
      ) : null}

      {/* Message box — hidden for archived (read-only) conversations */}
      {vm.selectedConversation.isArchived ? (
        // key forces remount on conversation change so the banner resets after dismiss
        <LDSMessages
          key={vm.selectedId}
          id="conversation-closed-banner"
          severity="warning"
          summary="This conversation has been closed"
          sticky={true}
          subtle={true}
          closable={true}
          className="lds-shrink-0 lds-mx-4 lds-my-3"
        />
      ) : (
        <div className="lds-shrink-0 lds-px-4 lds-py-3 lds-bg-white">
          <MessageBox onSend={vm.onSendMessage} onTyping={vm.onTyping} reconnecting={!vm.isSocketReady} />
        </div>
      )}
      {vm.renameDialog && (
        <RenameGroupDialog
          visible={vm.renameDialog.visible}
          currentName={vm.renameDialog.currentName}
          onSave={vm.renameDialog.onSave}
          onCancel={vm.renameDialog.onCancel}
        />
      )}
      {vm.participantsDialog && (
        <ViewParticipantsDialog
          visible={vm.participantsDialog.visible}
          participants={vm.participants}
          onCancel={vm.participantsDialog.onCancel}
        />
      )}
      {vm.confirmDialog && (
        <ActionConfirmation
          visible={vm.confirmDialog.visible}
          title={vm.confirmDialog.title}
          description={vm.confirmDialog.description}
          confirmLabel={vm.confirmDialog.confirmLabel}
          confirmSeverity={vm.confirmDialog.confirmSeverity}
          warningHeader={vm.confirmDialog.confirmSeverity === "danger"}
          onConfirm={vm.confirmDialog.onConfirm}
          onCancel={vm.confirmDialog.onCancel}
        />
      )}
    </div>
  );
};

export default MessagePanel;
